import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, Flex, Tag, Typography } from 'antd';
import dayjs from 'dayjs';

import { parseNegativeNumber } from '../../lib/parse-negative-number';
import { useGetTradingPlatformSessionCurrency } from '../../models/selectors/get-trading-platform-session-currency';
import type { CalendarData } from '../../models/types/transformed-calendar-data';

import cls from './trading-platform-trade-info-card.module.css';

interface TradingPlatformTradeInfoCardProps {
    data: CalendarData['trades'][number];
}

export const TradingPlatformTradeInfoCard = memo(
    (props: TradingPlatformTradeInfoCardProps) => {
        const { data } = props;

        const { t } = useTranslation('trading-journal');

        const currency = useGetTradingPlatformSessionCurrency();

        return (
            <Card
                className={cls.root}
                size="small"
                title={
                    <Flex justify="space-between">
                        <Flex
                            align="center"
                            gap={8}
                        >
                            <Typography.Text className={cls.tickerName}>
                                {data.symbol}
                            </Typography.Text>
                            <Typography.Text className={cls?.time}>
                                {dayjs(data.open_time * 1000).format(
                                    'HH:mm:ss',
                                )}
                            </Typography.Text>
                            <Typography.Text className={cls?.time}>
                                -
                            </Typography.Text>
                            <Typography.Text className={cls?.time}>
                                {dayjs(data.close_time * 1000).format(
                                    'HH:mm:ss',
                                )}
                            </Typography.Text>
                        </Flex>

                        <Tag
                            className={cls[data.type]}
                            color={data.type === 'sell' ? 'red' : 'blue'}
                        >
                            {t(
                                data.type === 'sell'
                                    ? 'tradingJournalTradeOperationSell'
                                    : 'tradingJournalTradeOperationBuy',
                            )}
                        </Tag>
                    </Flex>
                }
            >
                <Flex
                    gap={8}
                    vertical
                >
                    <Flex
                        align="center"
                        justify="space-between"
                    >
                        <Typography.Text className={cls.title}>
                            {t('tradingJournalTradeInfoCardTitleVolume')}
                        </Typography.Text>
                        <Typography.Text className={cls.value}>
                            {data.volume}
                        </Typography.Text>
                    </Flex>

                    <Flex
                        align="center"
                        justify="space-between"
                    >
                        <Typography.Text className={cls.title}>
                            {t('tradingJournalTradeInfoCardTitleNetProfit')}
                        </Typography.Text>
                        <Typography.Text className={cls.value}>
                            {parseNegativeNumber(data.net_profit, currency)}
                        </Typography.Text>
                    </Flex>

                    <Flex
                        align="center"
                        justify="space-between"
                    >
                        <Typography.Text className={cls.title}>
                            {t('tradingJournalTradeInfoCardTitleNetRoi')}
                        </Typography.Text>
                        <Typography.Text className={cls.value}>
                            {data.net_roi}%
                        </Typography.Text>
                    </Flex>

                    {data.commission !== undefined &&
                        data.commission !== null && (
                            <Flex
                                align="center"
                                justify="space-between"
                            >
                                <Typography.Text className={cls.title}>
                                    {t(
                                        'tradingJournalTradeInfoCardTitleCommission',
                                    )}
                                </Typography.Text>
                                <Typography.Text className={cls.value}>
                                    {parseNegativeNumber(
                                        data.commission,
                                        currency,
                                    )}
                                </Typography.Text>
                            </Flex>
                        )}
                </Flex>
            </Card>
        );
    },
);

TradingPlatformTradeInfoCard.displayName = 'TradingPlatformTradeInfoCard';
